import React, { useMemo, useState } from "react";

import { useResetIdFraudRiskCheck } from "shared/hooks/api";
import useModal from "shared/hooks/useModal";
import { Checkbox, Select, TextArea } from "shared/components/Form";
import { Modal } from "shared/components/Modal";
import Spacer from "shared/components/Spacer";
import Well from "shared/components/Well";
import { RegularText, SmallText } from "shared/components/Typography";
import Icon from "shared/components/Icon";
import { RedoIcon } from "shared/components/vega/icons";

export const SENTILINK_RESET_REASONS = [
  "Applicant entered incorrect SSN",
  "Applicant reported identity theft / disputed result",
  "Identity check system error / timeout",
  "Duplicate application — re-verification needed",
  "Other",
] as const;

const OTHER_REASON = "Other";

export interface ResetIdFraudRiskModalContext {
  applicationId: string | number;
  applicantName?: string;
  applicantRole?: string;
  applicantEmail?: string;
  riskLabel?: string;
  onSuccess?: () => void;
}

export const ResetIdFraudRiskModal: React.FC = () => {
  const { currentModalContext, selfApi } = useModal();
  const {
    applicationId,
    applicantName,
    applicantRole,
    applicantEmail,
    riskLabel,
    onSuccess,
  } = (currentModalContext || {}) as ResetIdFraudRiskModalContext;

  const [reason, setReason] = useState<string | undefined>(undefined);
  const [details, setDetails] = useState("");
  const [notify, setNotify] = useState(true);
  const [showErrors, setShowErrors] = useState(false);

  const { resetIdFraudRiskCheck, isResettingIdFraudRiskCheck } =
    useResetIdFraudRiskCheck({
      onSuccess: () => {
        if (onSuccess) {
          onSuccess();
        }
        selfApi.closeDialog();
      },
    });

  const detailsRequired = reason === OTHER_REASON;
  const reasonError =
    showErrors && !reason ? "Please select a reason." : undefined;
  const detailsError =
    showErrors && detailsRequired && !details.trim()
      ? "Additional details are required when the reason is \u201COther\u201D."
      : undefined;

  const subtitle = useMemo(() => {
    if (!applicantName) {
      return undefined;
    }
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        <span>{applicantName}</span>
        {applicantRole && (
          <>
            <span aria-hidden="true" style={{ color: "#9ca3af" }}>
              {"\u00b7"}
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "2px 10px",
                borderRadius: "999px",
                background: "#eef0f4",
                color: "#4b5563",
                fontSize: "12px",
                fontWeight: 500,
                lineHeight: "18px",
              }}
            >
              {applicantRole}
            </span>
          </>
        )}
      </span>
    );
  }, [applicantName, applicantRole]);

  const titleNode = (
    <span style={{ fontSize: "19px", fontWeight: 600, lineHeight: "27px" }}>
      Reset Identity Risk Check
    </span>
  );

  const titleIcon = (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        background: "#fce7e7",
        border: "1px solid #f3c6c6",
        flexShrink: 0,
        marginRight: "12px",
      }}
    >
      <RedoIcon
        aria-hidden="true"
        style={{
          width: "20px",
          height: "20px",
          marginRight: 0,
          fill: "#c0392b",
        }}
      />
    </span>
  );

  const handleSubmit = () => {
    if (isResettingIdFraudRiskCheck) {
      return;
    }

    setShowErrors(true);
    if (!reason || (detailsRequired && !details.trim())) {
      return;
    }
    resetIdFraudRiskCheck({
      applicationId,
      reason,
      details: details.trim(),
      notify,
    });
  };

  return (
    <Modal
      title={titleNode}
      titleIcon={titleIcon}
      subtitle={subtitle}
      submitButtonLabel="Reset check"
      cancelLinkLabel="Cancel"
      submit={handleSubmit}
      closeOnSubmit={false}
      submitting={isResettingIdFraudRiskCheck}
      submitButtonDisabled={isResettingIdFraudRiskCheck}
      centered
    >
      <Modal.Body>
        <Well lightWarning>
          <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
            <Icon.ErrorTriangleOutlineIcon
              aria-hidden="true"
              style={{
                width: "20px",
                height: "20px",
                flexShrink: 0,
                marginTop: "2px",
              }}
            />
            <div>
              <RegularText style={{ fontSize: "14px" }}>
                This clears the current{" "}
                {riskLabel ? <strong>{riskLabel}</strong> : "identity risk"} result
                so the applicant can re-enter their SSN and retry the Identity
                Risk Check. The previous result is retained for audit.
              </RegularText>
              <Spacer size={Spacer.SIZES.xs} />
              <RegularText strong style={{ fontSize: "14px" }}>
                Government ID & Selfie verification will NOT be reset.
              </RegularText>
            </div>
          </div>
        </Well>

        <Spacer size={Spacer.SIZES.default} />

        <Select
          id="idfraudrisk-reset-reason"
          name="reason"
          label="Reason for reset"
          value={reason}
          required
          error={reasonError}
          onChange={(value: string) => setReason(value)}
        >
          {SENTILINK_RESET_REASONS.map((option) => (
            <Select.Option key={option} value={option}>
              {option}
            </Select.Option>
          ))}
        </Select>

        <Spacer size={Spacer.SIZES.default} />

        <TextArea
          id="idfraudrisk-reset-details"
          name="details"
          label={
            detailsRequired
              ? "Additional details"
              : "Additional details (optional)"
          }
          value={details}
          required={detailsRequired}
          error={detailsError}
          maxLength={2048}
          showCount
          rows={4}
          onChange={(event) => setDetails(event.target.value)}
        />

        <Spacer size={Spacer.SIZES.default} />

        <Checkbox
          id="idfraudrisk-reset-notify"
          checked={notify}
          onChange={(event) => setNotify(event.target.checked)}
          label="Notify applicant their step is ready to retry"
        />
        {notify && (
          <div style={{ marginTop: "4px" }}>
            <SmallText light style={{ fontSize: "11px" }}>
              {applicantEmail
                ? `Sends an email to ${applicantEmail}`
                : "Sends an email to the applicant"}
            </SmallText>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ResetIdFraudRiskModal;
